import { AppError } from '@app/errors/AppError'

import { ExclusiveLock } from '@app/utils/ExclusiveLock'
import { createLogger } from '@app/utils/loggerCreator'
import { waitFor } from '@app/utils/waitFor'

import type { FirebaseManager } from './FirebaseManager'

export class FirebaseMessagingManager {
  private firebaseManager: FirebaseManager
  private lock = new ExclusiveLock<'connect' | 'disconnect'>()
  private subscribeLock = new ExclusiveLock<'subscribe' | 'unsubscribe'>()

  constructor(firebaseManager: FirebaseManager) {
    this.firebaseManager = firebaseManager
  }

  static async updateServiceWorker() {
    const regs = await navigator.serviceWorker.getRegistrations()
    try {
      this.log.log('updateServiceWorker. Found registrations: ', regs)
      const reg = regs.find(r => r.scope.endsWith('/firebase-cloud-messaging-push-scope'))
      if (reg) {
        this.log.log('updateServiceWorker. Found registration: ', reg)
        await reg?.update()
        this.log.log('updateServiceWorker. Updated service worker')
      } else {
        this.log.log('updateServiceWorker. Registration not found')
      }
    } catch (e) {
      console.error('updateServiceWorker. Failed to update service worker: ', e)
      throw e
    }
  }

  async isSupported() {
    const messaging = await import('firebase/messaging')
    return messaging.isSupported()
  }

  async connect() {
    return this.lock.run('connect', async (ab, stack) => {
      this.log.log('Connecting...')
      const [messaging, isSupported] = await Promise.all([import('firebase/messaging'), this.isSupported(), this.firebaseManager.connect()])
      ab.signal.throwIfAborted()

      if (!isSupported) {
        throw new FirebaseMessagingManagerUnsopportedError('Firebase messaging is not supported in this browser').withHandled(true)
      }

      stack.adopt(
        messaging.onMessage(messaging.getMessaging(), payload => {
          this.log.log('Message', payload)
        }),
        unsub => {
          unsub()
        }
      )

      this.log.log('Connected')
    })
  }

  async disconnect() {
    return this.lock.run('disconnect', async () => {
      this.log.log('Disconnected')
    })
  }

  async subscribe() {
    return this.subscribeLock
      .run('subscribe', async ab => {
        this.log.log('Subscribing...')
        const [m] = await Promise.all([
          await import('firebase/messaging'),
          FirebaseMessagingManager.updateServiceWorker(),
          waitFor(() => this.lock?.type === 'connect'),
        ])
        ab.signal.throwIfAborted()
        const token = await m.getToken(m.getMessaging())
        ab.signal.throwIfAborted()
        this.log.log('Subscribed. token:', token)
        return token
      })
      .catch(e => {
        this.subscribeLock.abort(`FirebaseMessagingManager subscribe failed: ${e instanceof Error ? e.message : String(e)}`)
        throw e
      })
  }

  async unsubscribe() {
    return this.subscribeLock.run('unsubscribe', async ab => {
      this.log.log('Unsubscribing...')
      const [m] = await Promise.all([await import('firebase/messaging'), waitFor(() => this.lock?.type === 'connect')])
      ab.signal.throwIfAborted()
      const result = await m.deleteToken(m.getMessaging())
      ab.signal.throwIfAborted()
      this.log.log('Unsubscribed. Success:', result)
      return result
    })
  }

  private log = createLogger('FirebaseMessaging', 'orange', 'bold')
  private static log = createLogger('FirebaseMessaging', 'orange', 'bold')
}

export class FirebaseMessagingManagerUnsopportedError extends AppError {}
